import { message, type AccountMode, type MessageKey } from '@sl/core';
import type { IconName } from '../../ui/Icon';

// Every word the global shell renders, in one place (T-036).
//
// Rule: a sentence that has a key in `data/tables/message-keys.json` is NEVER retyped here — the chip labels
// are `mode.*`, the freshness label is `state.*`. What is left are the shell's own control labels and the mode
// explainer, which no key covers yet; they are written to the voice rules (DESIGN_SYSTEM §2) and pass the SC-5
// glossary scan (a test renders the whole shell and checks the fail list).

/** REQ-006 AC1: the four chip labels, spelled by the catalogue. A demo label never contains "Live". */
export const MODE_MESSAGE_KEY: Record<AccountMode, MessageKey> = {
  demo: 'mode.demo',
  demo_view_only_preview: 'mode.demo_view_only',
  live_view_only: 'mode.live_view_only',
  live_editing: 'mode.live_editing',
};

export function modeMessage(mode: AccountMode) {
  return message(MODE_MESSAGE_KEY[mode]);
}

/**
 * REQ-006 AC3: the four states are distinct without relying on colour alone — **"(icon + label)"**, the
 * requirement's own words. QA's SC-6 enforces the icon on every screen and every overlay
 * (`standing/mode-chip.spec.ts`: *"the chip has no icon — colour alone is not enough"*).
 *
 * **The collision is settled, and the answer was the third way out** (BUG-059). Design QA 1 asked for dot
 * + words only, because the glyph here used to be `graduation-cap` — which is also the Learning pill's
 * (`ui/Pill/StatusPill.tsx`). T-049 removed the icon on that reading and SC-6 went red on 10 web projects:
 * a design note cannot overrule an acceptance criterion. The product-manager ruled the icon stays (D-129)
 * and the designer drew a new glyph for the demo states rather than moving Learning's (**D-137**):
 * **`flask`**, meaning *sample*, now in `ui/Icon` from `mockups/_shared/kit.js`. So one picture carries one
 * meaning again, and REQ-006 AC3 keeps its mechanism. `graduation-cap` is the Learning status's and only
 * that; `live_view_only` keeps the lock and `live_editing` the pencil (DESIGN_SYSTEM §12.2).
 */
export const MODE_ICON: Record<AccountMode, IconName> = {
  demo: 'flask',
  demo_view_only_preview: 'flask',
  live_view_only: 'lock',
  live_editing: 'pencil',
};

/** The dot's family, carried as `data-mode-family` so a test can assert the demo family without a colour. */
export const MODE_FAMILY: Record<AccountMode, 'demo' | 'live'> = {
  demo: 'demo',
  demo_view_only_preview: 'demo',
  live_view_only: 'live',
  live_editing: 'live',
};

/** The chip's tooltip — the mockup's own words (`web-shell.html`). */
export const MODE_CHIP_HINT: Record<AccountMode, string> = {
  demo: 'Sample account. Try anything — nothing here is real.',
  demo_view_only_preview: 'Sample account, behaving like a real account you can only look at.',
  live_view_only: 'Your real account. AdOrchestrator can look, not change.',
  live_editing: 'Your real account. Changes you publish go live.',
};

/**
 * REQ-006 AC2: what the current mode means for looking, planning and publishing (content per REQ-100 AC1).
 * It lives inside the `explainer.mode` bubble, which the SC-5 scan skips — but it is written to pass anyway.
 */
export interface ModeExplainer {
  title: string;
  lines: readonly string[];
}
export const MODE_EXPLAINER: Record<AccountMode, ModeExplainer> = {
  demo: {
    title: 'You are looking at sample data',
    lines: [
      'Juniper Home Goods is a made-up shop with 90 days of made-up history. Nothing here belongs to anyone and nothing you do reaches Meta.',
      'Everything works: change a budget, turn something off, publish it, undo it. The changes land on the sample data and stay until you reset.',
    ],
  },
  demo_view_only_preview: {
    title: 'This is how a view-only account behaves',
    lines: [
      'This is how AdOrchestrator behaves on a real account that is connected view-only. Nothing here is real.',
      'You can still plan changes and see what they would do; publishing is refused, and you get a checklist to work through by hand.',
    ],
  },
  live_view_only: {
    title: 'Your real account, read-only',
    lines: [
      'These are your real numbers, read from Meta. AdOrchestrator cannot change your ads.',
      'You can plan changes and see what they would do; publishing is off for this account, so you apply them yourself and mark them done.',
    ],
  },
  live_editing: {
    title: 'Your real account, with publishing on',
    lines: [
      'These are your real numbers, read from Meta.',
      'Changes you publish are sent to your account. You confirm every publish and can undo it straight after.',
    ],
  },
};

// ---- the shell's own control labels -------------------------------------------------------------------

export const SHELL_COPY = {
  skipLink: 'Skip to content',
  navLabel: 'Main',
  accountLabel: 'Ad account',
  modeChipTrigger: 'Mode and demo controls',
  modeMenuTitle: 'Mode',

  /**
   * THE ACCOUNT SWITCH (T-283). The account row was "a label in MVP, not a switcher — there is no second
   * account to move to"; T-282 made that untrue. A demo build can now serve either the committed demo or the
   * real account the pull wrote beside it, and the only way in was an address a founder has to remember. So
   * the row is the trigger of a menu listing what there is, with the one on screen marked.
   *
   * The demo's NAME is needed for one row only: the demo entry as seen FROM the live account, where the session
   * cannot tell us what the demo is called. Whenever the demo is the account on screen the label is built from
   * the session's own name instead, so the two can never disagree. T-287 deleted the constant D-383 put here
   * for `@sl/core`'s `DEMO_ACCOUNT_NAME` (0.3.8, on the ROOT entry — the shell must not import
   * `@sl/core/demo`, which is the whole demo chunk, ARCH §8.1), so the sample account has one name on the web,
   * on the phone and in the dataset it is read from.
   */
  accountSwitchTrigger: 'Change account',
  accountSwitchTitle: 'Ad account',
  /** The suffix the switch puts after the session's OWN name while the demo is the account on screen. */
  demoAccountSuffix: ' (demo)',
  /** A pulled account: its own name, and what AdOrchestrator may do with it. */
  liveAccountSuffix: ' — read-only',
  accountCurrent: 'Current',
  /** How old the pull is, in the product's own age words ("2 h ago"). */
  accountPulled: (ago: string): string => `pulled ${ago}`,
  accountPulledUnknown: 'pulled',
  /** Both notes on the row you are already on. */
  accountCurrentAnd: (note: string): string => `Current · ${note}`,
  /** While the app is looking for a pulled account beside it — never "connect" before we know. */
  accountLooking: 'Looking for your account…',

  /**
   * DESIGN_SYSTEM §12.2's ONE-TIME BANNER on a live, view-only account — "stated calmly, in neutral colours
   * (it's a fact, not an error)". T-210.
   *
   * **Its two sentences are not written here, on purpose.** The lead is the catalogue's own chip label
   * (`mode.live_view_only` → "Live · View-only", through `modeMessage`), so the banner and the chip can never
   * word the same fact differently; the sentence under it is `MODE_EXPLAINER.live_view_only.lines[0]`, which
   * already carries §12.2's "AdOrchestrator cannot change your ads." and is the same sentence the mode menu shows
   * when you ask it what this mode means. So the only new word on the surface is this one — the dismissal —
   * and it is the word this product already uses for "I have read it, hide it" (`BRIEFING_COPY.gotIt`,
   * w02's own dismissal in "Going well"), spelled again here because the shell may not import route copy
   * (`shell/bundle.test.ts`: the top bar's chunk holds what the first paint needs and nothing else).
   */
  viewOnlyDismiss: 'Got it',

  /**
   * **T-211 — the shell's own half of the live-data banners.** D-540's rule shapes all three: WHAT HAPPENED is
   * always the catalogue's sentence or the server's own, and what it MEANS for the ads and what to do next has
   * no message key and stays in the app.
   *
   * **Two of these three are not new words.** `retry` is this product's one retry verb, already written in ten
   * route tables, re-spelled here because the shell may not import route copy (`shell/bundle.test.ts`) — the
   * same reason `viewOnlyDismiss` above is spelled twice. `notAffectedHere` is the sentence `ConnectionBand`
   * has carried since T-048 for `token_invalid` / `permission_missing`, given a name so that REQ-283 AC2's
   * three-hour banner says the same thing instead of a second version of it; the band still reads it from here,
   * so there is one sentence and two readers rather than two sentences.
   */
  retry: 'Try again',
  notAffectedHere: 'Your ads and budgets are not affected — only what we can show you here.',
  /**
   * REQ-002 AC2's own clause — "results back to 14 Aug…" — and **the one sentence this row adds anywhere.**
   * D-1078 checked the catalogue key by key for every state T-211 draws and found each one covered; how far
   * back a first sync has reached is the exception, because no key carries a date for it (`warning.history_limited`
   * is about the PLAN's depth and would say something false). So it is the app's own, like every "what this
   * means" line in this table.
   *
   * The day is formatted `date.short` ("Aug 14") rather than REQ-002's own day-first spelling, because
   * `tests/fixtures/glossary.json`'s `day-first-date` rule fails on "14 Aug" (SC-5). T-214 takes it verbatim
   * (SC-2 reads phone → web).
   */
  firstSyncHistory: (day: string): string => `Results back to ${day} so far.`,

  refresh: 'Refresh',
  /** REQ-283 AC5: on a demo there is nothing to fetch, and the control says what it does do. */
  refreshHintDemo:
    'Sample data — there is nothing to fetch. Refresh re-checks alerts and staged changes.',
  refreshHintLive: 'Check Meta for newer numbers.',
  refreshDone: 'Numbers re-checked.',
  /**
   * THE LIVE LOOK'S REFRESH (T-283). On the live look the app has no transport to Meta at all — the numbers
   * come from `tools/meta-pull.mjs` and are exactly as old as the last pull. So neither the hint nor the toast
   * may promise newer numbers: Refresh here re-runs the alert evaluation over the same data, and that is worth
   * a control because a rule you just wrote can then be tested against your real account. Saying "Numbers
   * re-checked." after a press that fetched nothing would be the plainest kind of lie a screen can tell.
   */
  refreshHintPull:
    'These numbers are from the last pull. Refresh re-checks your alerts against them.',
  refreshDonePull: 'Alerts re-checked. The numbers are still from the last pull.',
  /** REQ-246, BUG-016's web half: what the shell says when it could not open BECAUSE the device has no
   *  connection. Plain and short, like the neighbouring "We couldn't open your account" it replaces —
   *  the catalogue's `state.offline` is the band's sentence and ends "showing what we last saved", which
   *  is not true on a cold start with nothing cached. */
  offlineTitle: 'You are offline',
  /** REQ-246: the offline band's own sentence. One definition — the band says it over data, and the
   *  shell says it in place of the page when the session itself could not load. */
  offlineBody: 'Your ads keep running as normal — we will catch up as soon as you are back online.',
  resetDemo: 'Reset demo',
  resetDemoTitle: 'Reset the demo?',
  resetDemoBody:
    'The sample account goes back to how it started: the original budgets, the original history, and no changes of yours. Nothing outside this demo is touched.',
  resetDemoConfirm: 'Reset demo',
  resetDemoCancel: 'Keep my changes',
  resetDemoWorking: 'Resetting…',
  resetDemoDone: 'The demo is back to how it started.',
  /**
   * START FRESH (REQ-001 AC6) — the other way back, and the one that takes the story away.
   *
   * "Reset demo" puts the sample story back as it was shipped; "Start fresh" hands the same ad account over
   * with nothing set on it, which is what a brand-new customer sees on their first morning: no monthly plan,
   * no guardrail, no alert rules, an empty inbox, nothing staged, and the first-run welcome again. The ads
   * and their history are the account's own and do not move.
   *
   * These seven strings are the phone's too. T-242 deleted mobile's `startFresh*` when D-128 took the dead
   * control off that build (BUG-029), and T-060's note asked the web to mirror them into `SHELL_COPY` when
   * its own control became real — which is here. **T-105 takes them back from this table, character for
   * character**, so the confirm reads the same on a desk and in a hand.
   */
  startFresh: 'Start fresh',
  startFreshTitle: 'Start fresh?',
  startFreshBody:
    'Everything you set in the demo goes back to the empty account: no monthly plan, no guardrail, no alert rules, an empty inbox and nothing staged. The sample ads and their history stay as they are.',
  startFreshConfirm: 'Start fresh',
  startFreshCancel: 'Keep what I have',
  startFreshWorking: 'Starting fresh…',
  startFreshDone: 'You are starting fresh — the demo account has nothing set on it now.',
  // `startFreshWhere` is GONE (T-117-W5). T-099 exported it "for the phone to take or leave" and neither
  // build ever rendered it — the confirm's body already says what the account becomes. A string with no
  // surface is not copy, it is a decision nobody has made yet.
  previewViewOnly: 'Preview View-only',
  backToFullDemo: 'Back to full demo',
  previewOn: 'Now behaving like a view-only account.',
  previewOff: 'Back to the full demo.',
  /**
   * THE RETURN VISIT (T-330, BUG-194 — REQ-023 AC1 v2.6, D-821). One control with two states, like the
   * preview above: the first reloads the demo as `?demoScenario=returning`, where the server keeps the story's
   * last visit (Sun, Sep 20, 8:40 am) so Home opens on "Since your last visit" — $299.00 spent · 13 purchases ·
   * 2 new alerts · no changes published; the second takes the switch off the address again. On the plain story
   * that panel shows once, on the first load of a fresh profile, and then not for 12 h (D-104 (3)), which is why
   * a prospect reading the gallery never saw it. The phone takes both strings verbatim (`copy.parity.test.ts`).
   */
  previewReturnVisit: 'Preview a return visit',
  endReturnVisit: 'End the return visit',
  /**
   * **THE WAY BACK OUT OF THE DEMO, ON A BUILD THAT HAS AN ACCOUNT** (T-432 — REQ-002 AC1 at REQUIREMENTS
   * v2.9, T-381 (g) / D-1127 (g)).
   *
   * Until this row the only way back from a demo session on an API build was `?server=api` in the address or
   * clearing `sl.server` by hand: "it is a one-way door with a workaround, and the owner of the account is the
   * person likeliest to be on the wrong side of it" (AC1's own sentence). So the way back is a CONTROL, and it
   * lives where REQ-001 AC5 already puts the things that change which account you are looking at.
   *
   * It is drawn on exactly one state — an API build showing the in-memory demo — so a demo build never grows a
   * row that would do nothing (D-128's rule), and a build already on its backend has nothing to switch to. The
   * phone takes this string verbatim (`copy.parity.test.ts`); it is app copy and mints no catalogue key.
   */
  switchToMyAdAccount: 'Switch to my ad account',
  alerts: 'Alerts',
  /**
   * The phone tab bar's fifth slot (DESIGN_SYSTEM §10 / §11, REQ-240 AC5). It is not a destination — it is
   * the way in to the destinations the other four tabs do not carry, which below 768 px have no side nav to
   * live in. The words are the phone app's.
   */
  more: 'More',
  moreTitle: 'More',
  moreSubtitle: 'Everything the tabs below do not carry.',
} as const;

/** The freshness label's key: a demo says "Sample data" and never "Updated n min ago" (REQ-283 AC5). */
export const FRESHNESS_KEY = {
  sample: 'state.sample_data',
  updated: 'state.updated',
  refreshing: 'state.refreshing',
  firstSync: 'state.first_sync',
} as const satisfies Record<string, MessageKey>;
